import { useEffect, useMemo, useState } from 'react'
import { checkHandle, reserveHandle, type HandleCheckApiResponse } from '../../api/handles'
import { getToken } from '../../lib/auth'
import { useMe } from '../../hooks/useMe'
import { getWalletSummary, getPixePrice } from '../../api/wallet'
import { purchaseAndClaimPremium } from '../../flows/handlePurchaseFlow'

type LookupStatus = 'idle' | 'loading' | 'invalid' | 'error' | 'available' | 'reserved' | 'claimed' | 'blocked' | 'review'

const normalize = (s: string) => {
  const trimmed = s.trim().replace(/^@+/, '').toLowerCase()
  const cleaned = trimmed.replace(/[^a-z0-9_.-]/g, '')
  return cleaned.replace(/^[_.-]+|[_.-]+$/g, '')
}
const isValid = (s: string) => /^[a-z0-9][a-z0-9_.-]{1,28}[a-z0-9]$/.test(s)

const STATUS_LABEL: Record<LookupStatus, string> = {
  idle: 'Waiting',
  loading: 'Checking…',
  invalid: 'Invalid',
  error: 'Error',
  available: 'Available',
  reserved: 'Reserved',
  claimed: 'Claimed',
  blocked: 'Blocked',
  review: 'Review',
}

const STATUS_TONE: Record<LookupStatus, string> = {
  idle: 'text-white/70 border-white/10',
  loading: 'text-sky-200 border-sky-500/40',
  invalid: 'text-amber-200 border-amber-500/40',
  error: 'text-rose-200 border-rose-500/40',
  available: 'text-emerald-200 border-emerald-500/40',
  reserved: 'text-yellow-200 border-yellow-500/40',
  claimed: 'text-rose-200 border-rose-500/40',
  blocked: 'text-rose-200 border-rose-500/40',
  review: 'text-amber-200 border-amber-500/40',
}

function formatPrice(cents?: number | null) {
  if (cents == null) return '—'
  if (cents <= 0) return 'Free'
  const dollars = cents / 100
  return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatTier(tier?: string) {
  if (!tier) return '—'
  return tier.slice(0, 1).toUpperCase() + tier.slice(1)
}

function formatExpiry(expiresAt?: string | null) {
  if (!expiresAt) return null
  const dt = new Date(expiresAt)
  if (Number.isNaN(dt.getTime())) return null
  return dt.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function formatPixe2(v: unknown) {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

export default function SettingsHandles() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)

  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<LookupStatus>('idle')
  const [error, setError] = useState<string>('')
  const [result, setResult] = useState<HandleCheckApiResponse | null>(null)
  const [reservationId, setReservationId] = useState<number | null>(null)
  const [holdExpiresAt, setHoldExpiresAt] = useState<number>(0)
  const [actionError, setActionError] = useState<string>('')
  const [actionMessage, setActionMessage] = useState<string>('')
  const [reserving, setReserving] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [myHandle, setMyHandle] = useState<string | null>(null)
  const [walletPixe, setWalletPixe] = useState<number | null>(null)
  const [pixeUsd, setPixeUsd] = useState<number>(1)

  const normalized = useMemo(() => normalize(query), [query])
  const priceLabel = formatPrice(result?.price)
  const tierLabel = formatTier(result?.tier)
  const expiryLabel = formatExpiry(result?.expires_at)
  const isPremium = (result?.price ?? 0) > 0
  const requiredPixe = result?.price ? (result.price / 100) / (pixeUsd || 1) : 0
  const deficitPixe = walletPixe == null ? null : Math.max(0, requiredPixe - walletPixe)

  useEffect(() => {
    if (me?.profile?.handle) {
      setMyHandle(me.profile.handle)
    } else if (me?.username) {
      setMyHandle(me.username)
    }
  }, [me])

  useEffect(() => {
    if (!reservationId || holdExpiresAt <= 0) return
    const tick = setInterval(() => {
      if (Date.now() > holdExpiresAt) {
        setReservationId(null)
        setHoldExpiresAt(0)
        setActionMessage('')
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [reservationId, holdExpiresAt])

  useEffect(() => {
    if (!authed) return
    ;(async () => {
      try {
        const [{ summary }, px] = await Promise.all([
          getWalletSummary().catch(() => ({ summary: { pixe_balance: '0', fiat_estimate: '0', change_24h_pct: '0', address: '' } })),
          getPixePrice().catch(() => 1),
        ])
        setWalletPixe(Number(summary.pixe_balance))
        setPixeUsd(px || 1)
      } catch {
        // ignore
      }
    })()
  }, [authed])

  useEffect(() => {
    const v = normalized
    if (!v) {
      setStatus('idle')
      setError('')
      setResult(null)
      return
    }
    if (!isValid(v)) {
      setStatus('invalid')
      setError('Use 3–30 chars: letters, numbers, underscores, hyphens, or dots.')
      setResult(null)
      return
    }

    let cancelled = false
      setStatus('loading')
      setError('')
      setActionError('')
      setActionMessage('')
      const timer = setTimeout(() => {
        const raw = query.trim() || v
        checkHandle(raw)
          .then((res) => {
            if (cancelled) return
          setResult(res)
          const nextStatus = (res.state?.toLowerCase() || 'error') as LookupStatus
          setStatus(nextStatus)
        })
        .catch((e: any) => {
          if (cancelled) return
          setResult(null)
          setStatus('error')
          setError(e?.response?.data?.detail || e?.message || 'Lookup failed')
        })
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [normalized, query])

  const onReserve = async () => {
    if (!result || !result.normalized) return
    setReserving(true)
    setActionError('')
    setActionMessage('')
    try {
      const res = await reserveHandle(result.normalized)
      setReservationId(res.reservation_id)
      setHoldExpiresAt(new Date(res.expires_at).getTime())
      setResult({
        ...result,
        state: 'reserved',
        expires_at: res.expires_at,
      })
      setStatus('reserved')
      setActionMessage('Handle reserved for 10 minutes.')
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Could not reserve handle.')
    } finally {
      setReserving(false)
    }
  }

  const onClaim = async () => {
    if (!reservationId || !result?.normalized) {
      setActionError('Reserve a handle first.')
      return
    }
    setClaiming(true)
    setActionError('')
    setActionMessage('')
    try {
      const res = await purchaseAndClaimPremium(reservationId)
      if (!res.ok) {
        if (res.deficit_pixe) setActionError(`Need ${res.deficit_pixe} more PIXE to purchase.`)
        else setActionError('Purchase completed but no payment reference was returned.')
        return
      }
      setMyHandle(result.normalized)
      setStatus('claimed')
      setReservationId(null)
      setHoldExpiresAt(0)
      setResult({ ...result, state: 'claimed', expires_at: null })
      setActionMessage('Handle claimed and linked to your profile.')
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not claim handle.'
      setActionError(detail)
      if (detail.toLowerCase().includes('expired') || detail.toLowerCase().includes('conflict')) {
        setReservationId(null)
      }
    } finally {
      setClaiming(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold">Handles</h1>
          <p className="text-sm text-white/70">
            Check availability and pricing for premium handles without leaving settings.
          </p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
          <div className="text-xs uppercase tracking-wide text-white/50">Your handle</div>
          <div className="font-semibold">{myHandle ? `@${myHandle}` : '—'}</div>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-white/70">Search</div>
          <div className="flex-1 flex items-center rounded-lg border border-white/10 bg-black/30">
            <span className="px-3 text-white/60">@</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="yourname"
              className="flex-1 bg-transparent px-2 py-2 text-white outline-none"
            />
          </div>
          <div className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_TONE[status]}`}>
            {STATUS_LABEL[status]}
          </div>
        </div>

        {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">{error}</div>}

        {result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-white/50">Normalized</div>
                <div className="text-lg font-semibold">@{result.normalized}</div>
                {result.breakdown && (
                  <div className="mt-2 text-xs text-white/60 space-y-1">
                    <div>Score: <span className="text-white/90 font-semibold">{result.score}</span></div>
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(result.breakdown).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between rounded bg-white/5 px-2 py-1">
                          <span className="uppercase tracking-wide text-[10px] text-white/60">{k}</span>
                          <span className="text-white/80 text-xs">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-white/50">Tier</div>
                <div className="text-lg font-semibold">{tierLabel}</div>
                <div className="mt-2 text-xs text-white/60">
                  {result.state === 'available' && 'Ready to reserve now.'}
                  {result.state === 'reserved' && 'On hold — try again after it expires.'}
                  {result.state === 'claimed' && 'Already claimed.'}
                  {result.state === 'blocked' && 'Restricted or blocked.'}
                  {result.state === 'review' && 'Under review.'}
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-white/50">Price</div>
                <div className="text-lg font-semibold">{priceLabel}</div>
                {expiryLabel && (
                  <div className="mt-2 text-xs text-white/60">Hold expires at {expiryLabel}</div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/70">Actions</div>
                <div className="text-right text-xs text-white/60 space-y-1">
                  {reservationId && holdExpiresAt > Date.now() && (
                    <div>Hold ends in {Math.max(0, Math.floor((holdExpiresAt - Date.now()) / 1000))}s</div>
                  )}
                  {walletPixe != null && (
                    <div>Balance: {formatPixe2(walletPixe)} PIXE</div>
                  )}
                  {isPremium && requiredPixe > 0 && (
                    <div>Price: {formatPixe2(requiredPixe)} PIXE</div>
                  )}
                </div>
              </div>

              {actionError && (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
                  {actionError}
                </div>
              )}
              {actionMessage && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
                  {actionMessage}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onReserve}
                  disabled={status !== 'available' || reserving}
                  className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 disabled:opacity-50"
                >
                  {reserving ? 'Reserving…' : 'Reserve handle'}
                </button>

                <button
                  onClick={onClaim}
                  disabled={!reservationId || holdExpiresAt <= Date.now() || claiming || (deficitPixe !== null && deficitPixe > 0)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                    isPremium ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'
                  } disabled:opacity-50`}
                >
                  {claiming
                    ? 'Purchasing…'
                    : isPremium
                      ? deficitPixe && deficitPixe > 0
                        ? `Need ${formatPixe2(deficitPixe)} PIXE`
                        : `Purchase for ${formatPixe2(requiredPixe)} PIXE`
                      : 'Claim free handle'}
                </button>
              </div>
              <div className="text-xs text-white/60">
                Step 1: Reserve to lock the price. Step 2: Claim to attach it to your account.
              </div>
            </div>
          </>
        )}

        {!result && status === 'idle' && (
          <div className="rounded-lg border border-dashed border-white/10 bg-black/10 px-3 py-6 text-center text-sm text-white/60">
            Start typing a handle to see availability and pricing.
          </div>
        )}
      </div>
    </div>
  )
}
